import React, { useMemo } from 'react';
import styled from 'styled-components';

import { GqlMenuSection, MenuSectionType, MobileAppFlair, MobileAppProductCollectionDisplayType } from 'types/graphql';
import { ProductCarouselV2 } from './product-carousel';
import { OfferCarousel } from './offer-carousel';
import { FlairWrapper } from '../flair-wrapper';

type CarouselsProps = {
  menuSections: GqlMenuSection[];
  flairColor?: string;
  asMobileApp?: boolean;
  buttonFlair?: MobileAppFlair;
};

type SectionProps = {
  menuSection: GqlMenuSection;
};

type ProductCarouselProps = SectionProps & {
  asMobileApp?: boolean;
  buttonFlair?: MobileAppFlair;
};

function OfferSection({ menuSection }: SectionProps): JSX.Element {
  return <OfferCarousel menuSection={menuSection} />;
}

function CustomSection({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.products) {
      throw new Error('CustomSection is missing products');
    }

    return {
      sortBy: `popularSortIdx`,
      productIds: menuSection.products,
    };
  }, [menuSection.products]);

  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

const Image = styled.img`
  padding: 20px;
  width: 100%;
  height: auto;
`;

function ImageSection({ menuSection }: SectionProps): JSX.Element {
  return <Image src={menuSection.image?.image ?? ''} alt={menuSection.image?.alt ?? ''} />;
}

function TopSellersSection({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const productsFilter = { sortBy: `popular` };

  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

function RecommendedForYouSection({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const productsFilter = { sortBy: `popular` };

  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

function RecentlyViewedSection({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.products) {
      throw new Error('RecentlyViewedSection is missing products');
    }

    return {
      sortBy: `popularSortIdx`,
      productIds: menuSection.products,
    };
  }, [menuSection.products]);

  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

function StaffPicksSection({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const productsFilter = {
    sortBy: `popularSortIdx`,
    staffPicks: true,
  };

  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

function SpecialsCarousel({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const productsFilter = { isOnSpecial: true };
  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

function SubCategoryCarousel({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.subcategory) {
      throw new Error('SubCategoryCarousel is missing subcategory');
    }

    return {
      sortBy: `popularSortIdx`,
      subcategories: [menuSection.subcategory],
    };
  }, [menuSection.subcategory]);

  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

function BrandCarousel({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.brandId) {
      throw new Error('BrandCarousel is missing brand');
    }

    return {
      sortBy: `popularSortIdx`,
      brandIds: [menuSection.brandId],
    };
  }, [menuSection.brandId]);

  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

function CategoryCarousel({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.category) {
      throw new Error('CategoryCarousel is missing category');
    }

    return {
      sortBy: `popularSortIdx`,
      types: [menuSection.category],
    };
  }, [menuSection.category]);

  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

function CollectionCarousel({ menuSection, asMobileApp, buttonFlair }: ProductCarouselProps): JSX.Element {
  const { sortBy, collectionId } = menuSection;
  const productsFilter = { sortBy, collectionId };

  return (
    <ProductCarouselV2
      menuSection={menuSection}
      productsFilter={productsFilter}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
    />
  );
}

const sectionTypeToCarousel: Record<MenuSectionType, React.FC<ProductCarouselProps>> = {
  [MenuSectionType.category]: CategoryCarousel,
  [MenuSectionType.brand]: BrandCarousel,
  [MenuSectionType.recentlyViewed]: RecentlyViewedSection,
  [MenuSectionType.recommendedForYou]: RecommendedForYouSection,
  [MenuSectionType.subcategory]: SubCategoryCarousel,
  [MenuSectionType.specials]: SpecialsCarousel,
  [MenuSectionType.staffPicks]: StaffPicksSection,
  [MenuSectionType.topSellers]: TopSellersSection,
  [MenuSectionType.custom]: CustomSection,
  [MenuSectionType.offers]: OfferSection,
  [MenuSectionType.collection]: CollectionCarousel,
  [MenuSectionType.image]: ImageSection,
};

export function Carousels({ menuSections, flairColor, asMobileApp, buttonFlair }: CarouselsProps): JSX.Element | null {
  return (
    <>
      {menuSections.map((section) => {
        const CarouselComponent = sectionTypeToCarousel[section.sectionType];
        const shouldShowFlair =
          section.mobileAppFlairType != null && section.mobileAppFlairType !== MobileAppFlair.none;

        if (
          asMobileApp &&
          section.mobileAppDisplayType === MobileAppProductCollectionDisplayType.grid &&
          !shouldShowFlair
        ) {
          return (
            <GridCardsContainer key={section.id} $color={flairColor}>
              <CarouselComponent menuSection={section} asMobileApp={asMobileApp} buttonFlair={buttonFlair} />
            </GridCardsContainer>
          );
        }

        if (flairColor != null && shouldShowFlair) {
          return (
            <FlairWrapper
              key={section.id}
              flairType={section.mobileAppFlairType ?? MobileAppFlair.none}
              color={flairColor}
            >
              <CarouselComponent menuSection={section} asMobileApp={asMobileApp} buttonFlair={buttonFlair} />
            </FlairWrapper>
          );
        }

        return (
          <React.Fragment key={section.id}>
            <CarouselComponent menuSection={section} asMobileApp={asMobileApp} buttonFlair={buttonFlair} />
          </React.Fragment>
        );
      })}
    </>
  );
}

const GridCardsContainer = styled.div<{ $color?: string }>`
  background-color: ${({ $color }) => $color ?? 'none'};
  border-radius: ${({ theme }) => theme.radius.lg};
  margin-bottom: 40px;
`;
